import {
  applyPatch,
  capabilitiesOf,
  emptyRoomState,
  type AckResult,
  type AttendeePermissions,
  type Capabilities,
  type ConnectionQuality,
  type JoinResult,
  type LayoutMode,
  type MediaKind,
  type Participant,
  type PeerId,
  type Role,
  type RoomState,
  type SfuAccess,
  type StartScreenSharePayload,
  type WaitingGuest,
} from '@live-stream/shared'
import type { Auth } from './auth.js'
import type { HttpClient } from './http.js'
import { clearMembership, loadMembership, loadResume } from './resume.js'
import { measureRoundTrip, type AppSocket, type SocketManager } from './socket.js'
import type { KeyValueStore } from './storage.js'
import { derived, patch, readonly, writable, type ReadableStore } from './store.js'

/**
 * The meeting: joining, the lobby, the room's shared state, and every
 * request a participant or a moderator makes.
 *
 * The server decides and broadcasts; this renders what comes back. Nothing
 * here optimistically mutates the room's state: guessing locally is how two
 * people end up seeing different spotlights. Versioned patches are applied
 * in order, and a gap or a server that went backwards triggers a resync.
 */

/** `ended`: a host ended the meeting for everyone; `message` says who. */
export type RoomPhase = 'idle' | 'connecting' | 'waiting' | 'joined' | 'denied' | 'error' | 'ended'

export interface RequestOutcome {
  ok: boolean
  error?: string
}

export interface RoomDiagnostics {
  patchesApplied: number
  patchesStale: number
  patchesGap: number
  resyncs: number
  speakingSent: number
  qualitySent: number
  sendFailures: number
  lastSendError: string | null
  lastPatchAt: number | null
}

export interface RoomControllerState {
  phase: RoomPhase
  /** The room as the server last said it is. */
  room: RoomState
  me: PeerId | null
  myRole: Role
  /** The server can caption this meeting (a transcription service is configured). */
  captionsAvailable: boolean
  /** Claude is configured too: the in-meeting assistant works while captions are on. */
  assistantAvailable: boolean
  /** Something to show: a refusal, a lobby denial, a restart notice. */
  message: string | null
  /** A host asked us to turn our mic or camera on; ours to accept. Null once answered. */
  asked: { kind: MediaKind; byName: string; at: number } | null
  /**
   * A host let us speak (`granted`), or moved us back to the audience: said
   * once, for the notice. Null once seen.
   */
  invited: { granted: boolean; byName: string; at: number } | null
  /** Known while waiting in the lobby, before the room's state is. */
  roomName: string
  /** The room we are in or waiting for, so a reconnect knows what to rejoin. */
  slug: string | null
  /** This meeting occurrence. Chat hangs off it; a reload lands in the same one. */
  sessionId: string | null
  /** Where and how to reach the SFU for this seat; null when the mesh carries the call. */
  sfu: SfuAccess | null
  /** The application plane only. Media flows on its own and does not wait for it. */
  link: 'online' | 'reconnecting'
  /** Bumped on every successful rejoin; media state is re-reported on it. */
  rejoinCount: number
  /**
   * Counters for a diagnostics panel. When a report says "her ring shows on
   * her screen but not on mine", these say which link broke: her sends
   * refused, our patches dropped, a resync storm.
   */
  diag: RoomDiagnostics
}

type IdentityHook = (peerId: PeerId, sock: AppSocket) => void

function initialState(): RoomControllerState {
  return {
    phase: 'idle',
    room: emptyRoomState(''),
    me: null,
    myRole: 'participant',
    captionsAvailable: false,
    assistantAvailable: false,
    message: null,
    asked: null,
    invited: null,
    roomName: '',
    slug: null,
    sessionId: null,
    sfu: null,
    link: 'online',
    rejoinCount: 0,
    diag: {
      patchesApplied: 0,
      patchesStale: 0,
      patchesGap: 0,
      resyncs: 0,
      speakingSent: 0,
      qualitySent: 0,
      sendFailures: 0,
      lastSendError: null,
      lastPatchAt: null,
    },
  }
}

export class Room {
  private readonly store = writable<RoomControllerState>(initialState())
  readonly state: ReadableStore<RoomControllerState> = readonly(this.store)
  /** The open socket while in a meeting; the media, chat and caption controllers attach to it. */
  readonly socket: ReadableStore<AppSocket | null>
  private readonly socketStore = writable<AppSocket | null>(null)
  /** The room as the server broadcasts it: participants, focus, screen share, recording, captions. */
  readonly shared: ReadableStore<RoomState>
  /** Everyone in the meeting, in the order they joined. */
  readonly participants: ReadableStore<Participant[]>
  readonly waitingRoom: ReadableStore<WaitingGuest[]>
  /** True for the host and co-hosts. */
  readonly moderator: ReadableStore<boolean>
  /** Our own seat in the room, once joined. */
  readonly self: ReadableStore<Participant | undefined>
  /** What attendees may do in this meeting: the host's switches. */
  readonly permissions: ReadableStore<AttendeePermissions>
  /**
   * What we may do ourselves: everything as a host or cohost; as an
   * attendee, what the switches allow — and our mic and camera when a host
   * let us speak. The server holds the same line.
   */
  readonly caps: ReadableStore<Capabilities>
  /** Attendees may not turn their mic on: a big meeting run as one. */
  readonly audienceMode: ReadableStore<boolean>
  private readonly identityHooks = new Set<IdentityHook>()
  private unwatchNetwork: (() => void) | null = null
  private healing = false

  constructor(
    private readonly http: HttpClient,
    private readonly auth: Auth,
    private readonly sockets: SocketManager,
    private readonly session: KeyValueStore | null,
  ) {
    this.socket = readonly(this.socketStore)
    const room = derived([this.store], (s) => s.room)
    this.shared = room
    this.participants = derived([room], (r) =>
      Object.values(r.participants).sort((a, b) => a.joinedAt - b.joinedAt),
    )
    this.waitingRoom = derived([room], (r) => r.waitingRoom)
    const me = derived([this.store], (s) => s.me)
    const role = derived([this.store], (s) => s.myRole)
    this.moderator = derived([role], (r) => r === 'host' || r === 'cohost')
    this.self = derived([room, me], (r, id) => (id ? r.participants[id] : undefined))
    this.permissions = derived([room], (r) => r.permissions)
    this.caps = derived([role, this.self, this.permissions], (myRole, self, permissions) =>
      capabilitiesOf({ role: myRole, speakGranted: self?.speakGranted }, permissions),
    )
    this.audienceMode = derived([this.permissions], (p) => !p.mic)
  }

  /** The current value of `moderator`. */
  get isModerator(): boolean {
    return this.moderator.get()
  }

  /** The current value of `self`. */
  get myParticipant(): Participant | undefined {
    return this.self.get()
  }

  private inRoom(): boolean {
    const phase = this.store.get().phase
    return phase === 'joined' || phase === 'waiting'
  }

  private setDiag(changes: Partial<RoomDiagnostics>): void {
    this.store.update((s) => ({ ...s, diag: { ...s.diag, ...changes } }))
  }

  private bump(
    key:
      | 'patchesApplied'
      | 'patchesStale'
      | 'patchesGap'
      | 'resyncs'
      | 'speakingSent'
      | 'qualitySent'
      | 'sendFailures',
  ): void {
    this.store.update((s) => ({ ...s, diag: { ...s.diag, [key]: s.diag[key] + 1 } }))
  }

  private wire(sock: AppSocket): void {
    sock.on('room:snapshot', (snapshot) => patch(this.store, { room: snapshot }))

    sock.on('room:patch', (p) => {
      const s = this.store.get()
      const result = applyPatch(s.room, p)
      this.setDiag({ lastPatchAt: Date.now() })
      if (result.outcome === 'applied') {
        this.bump('patchesApplied')
        patch(this.store, { room: result.state })
        return
      }
      // A gap means we missed one; applying a delta onto state we cannot
      // trust is how mute icons end up different for different people.
      if (result.outcome === 'gap') {
        this.bump('patchesGap')
        void this.resync()
        return
      }
      // Stale by one is a duplicate, and harmless. Stale by more means the
      // server is BEHIND us (it restarted, or rebuilt the room) and every
      // patch from now on would be dropped. Take the server's state instead.
      this.bump('patchesStale')
      if (p.version < s.room.version - 1) void this.resync()
    })

    sock.on('lobby:admitted', (a) =>
      patch(this.store, {
        room: a.state,
        me: a.you,
        myRole: a.role,
        sessionId: a.sessionId,
        sfu: a.sfu,
        captionsAvailable: a.captionsAvailable === true,
        assistantAvailable: a.assistantAvailable === true,
        phase: 'joined',
        message: null,
      }),
    )
    sock.on('lobby:denied', ({ reason }) => patch(this.store, { phase: 'denied', message: reason }))
    sock.on('room:ejected', ({ reason }) => patch(this.store, { phase: 'denied', message: reason }))
    // The meeting is over. The socket stays: a recording being saved
    // finishes over it; leave() lets it go.
    sock.on('room:ended', ({ byName, byPeerId }) => {
      const mine = byPeerId === this.store.get().me
      patch(this.store, {
        phase: 'ended',
        message: mine
          ? 'You ended the meeting for everyone.'
          : `${byName} ended the meeting for everyone.`,
      })
      clearMembership(this.session)
    })
    sock.on('speak:granted', ({ granted, byName }) =>
      patch(this.store, { invited: { granted, byName, at: Date.now() } }),
    )
    sock.on('media:asked', ({ kind, byName }) =>
      patch(this.store, { asked: { kind, byName, at: Date.now() } }),
    )

    sock.on('connect_error', (err) => {
      if (err.message === 'unauthenticated') {
        patch(this.store, {
          phase: 'error',
          message: 'Your pass for this meeting is no longer valid.',
        })
        return
      }
      // A failed attempt WHILE reconnecting is not the end: the client keeps
      // retrying with backoff, and the link state already says so.
      if (this.store.get().link === 'reconnecting') return
      patch(this.store, {
        phase: 'error',
        message: `Could not reach the meeting server (${err.message}).`,
      })
    })

    sock.on('disconnect', () => {
      if (this.inRoom()) patch(this.store, { link: 'reconnecting' })
    })

    let connectedOnce = false
    sock.on('connect', () => {
      if (!connectedOnce) {
        connectedOnce = true
        return
      }
      if (this.inRoom() && this.store.get().slug) void this.rejoin()
    })

    sock.on('server:shutdown', ({ reconnectInMs }) =>
      patch(this.store, {
        message: `The server is restarting. Reconnecting in ${Math.round(reconnectInMs / 1000)}s…`,
      }),
    )

    this.watchNetwork(sock)
  }

  /**
   * The OS knows before the socket does. Offline: say so at once instead of
   * waiting for the heartbeat. Online: the socket is dead by now; drop it and
   * connect fresh, and the server hands the seat back. And a page going away
   * (a closed tab, a reload) sends a beacon saying so, so the seat is kept
   * for seconds rather than the full grace a Wi-Fi blip gets.
   */
  private watchNetwork(sock: AppSocket): void {
    if (typeof window === 'undefined') return
    const onOffline = (): void => {
      if (this.inRoom()) patch(this.store, { link: 'reconnecting' })
    }
    const onOnline = (): void => {
      if (!this.inRoom()) return
      patch(this.store, { link: 'reconnecting' })
      sock.disconnect()
      sock.connect()
    }
    const onPageHide = (): void => {
      if (!this.inRoom()) return
      const creds = loadResume(this.session)
      if (!creds || typeof navigator.sendBeacon !== 'function') return
      navigator.sendBeacon(
        this.http.url('/session/leaving'),
        new Blob([JSON.stringify({ peerId: creds.peerId, token: creds.token })], {
          type: 'application/json',
        }),
      )
    }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    window.addEventListener('pagehide', onPageHide)
    this.unwatchNetwork = () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('pagehide', onPageHide)
    }
  }

  /**
   * Connect the socket and resolve once it has a peer id. Idempotent.
   *
   * Separate from join() so the media transport can subscribe to signaling
   * BEFORE room:join goes out: it needs the peer id to decide who is polite.
   */
  async ensureSocket(): Promise<{ sock: AppSocket; peerId: PeerId }> {
    patch(this.store, { phase: 'connecting', message: null })

    let sock = this.socketStore.get()
    if (!sock) {
      sock = this.sockets.connect(this.auth.socketToken)
      this.socketStore.set(sock)
      this.wire(sock)
    }

    if (!sock.connected) {
      const s = sock
      await new Promise<void>((resolve, reject) => {
        s.once('connect', () => resolve())
        s.once('connect_error', (err) => reject(err))
      })
    }
    return { sock, peerId: await this.sockets.waitForPeerId() }
  }

  async join(slug: string): Promise<void> {
    patch(this.store, { slug })
    let sock: AppSocket
    try {
      sock = (await this.ensureSocket()).sock
    } catch (err) {
      patch(this.store, {
        phase: 'error',
        message: `Could not reach the meeting server (${err instanceof Error ? err.message : String(err)}).`,
      })
      return
    }
    await this.requestJoin(sock, slug)
  }

  /** Emit room:join and apply the verdict. Shared by the first join and every rejoin. */
  private async requestJoin(sock: AppSocket, slug: string, rejoining = false): Promise<void> {
    const membership = loadMembership(this.session)
    const result = await new Promise<AckResult<JoinResult>>((resolve) => {
      sock.emit(
        'room:join',
        { slug, ...(membership ? { membership: membership.token } : {}) },
        resolve,
      )
    })

    patch(this.store, { link: 'online' })
    if (!result.ok) {
      patch(this.store, { phase: 'error', message: result.error.message })
      return
    }
    if (result.data.status === 'waiting') {
      patch(this.store, { phase: 'waiting', roomName: result.data.roomName })
      return
    }
    const d = result.data
    this.store.update((s) => ({
      ...s,
      room: d.state,
      me: d.you,
      myRole: d.role,
      sessionId: d.sessionId,
      sfu: d.sfu,
      captionsAvailable: d.captionsAvailable === true,
      assistantAvailable: d.assistantAvailable === true,
      phase: 'joined',
      rejoinCount: rejoining ? s.rejoinCount + 1 : s.rejoinCount,
    }))
  }

  /**
   * The socket came back. The same seat if the server kept it, a fresh join
   * otherwise; after a restart the membership claim walks us past the
   * lobby. If the server could not give our peer id back (a duplicated tab,
   * an expired resume token), the media plane is told BEFORE we join.
   */
  async rejoin(): Promise<void> {
    const sock = this.socketStore.get()
    const { slug, me } = this.store.get()
    if (!sock || !slug) return
    const peerId = await this.sockets.waitForPeerId()
    if (me && peerId !== me) for (const hook of this.identityHooks) hook(peerId, sock)
    await this.requestJoin(sock, slug, true)
  }

  /** For the rare reconnect that comes back under a new peer id. Returns the unsubscribe. */
  onIdentityChanged(hook: IdentityHook): () => void {
    this.identityHooks.add(hook)
    return () => this.identityHooks.delete(hook)
  }

  /**
   * Every media link dropped at once: that is our network, whatever the
   * socket believes. Ask the server; no answer in three seconds means the
   * socket is dead too, so reconnect fresh rather than wait for the heartbeat.
   */
  async probeConnection(): Promise<void> {
    const sock = this.socketStore.get()
    if (!sock || this.store.get().link !== 'online' || !this.inRoom()) return
    try {
      await measureRoundTrip(sock, 3_000)
    } catch {
      patch(this.store, { link: 'reconnecting' })
      sock.disconnect()
      sock.connect()
    }
  }

  /** Send an intent and surface the server's verdict; a refusal also becomes `state.message`. */
  request(
    emit: (sock: AppSocket, ack: (r: AckResult<void>) => void) => void,
  ): Promise<RequestOutcome> {
    return new Promise((resolve) => {
      const sock = this.socketStore.get()
      if (!sock) return resolve({ ok: false, error: 'Not connected.' })
      emit(sock, (result) => {
        if (result.ok) return resolve({ ok: true })
        patch(this.store, { message: result.error.message })
        resolve({ ok: false, error: result.error.message })
      })
    })
  }

  async resync(): Promise<void> {
    const sock = this.socketStore.get()
    if (!sock) return
    this.bump('resyncs')
    await new Promise<void>((resolve) => {
      sock.emit('room:resync', (result) => {
        if (result.ok) patch(this.store, { room: result.data })
        resolve()
      })
    })
  }

  /** Show a message (a refusal the app got elsewhere, say), or clear it with null. */
  setMessage(message: string | null): void {
    patch(this.store, { message })
  }

  /** Put the meeting in its error state with a reason: a join the app itself could not start. */
  fail(message: string | null): void {
    patch(this.store, { phase: 'error', message })
  }

  setMuted = (muted: boolean) =>
    this.request((s, ack) => s.emit('request:set-media-state', { audio: { muted } }, ack))
  /** Camera on or off; on, with its picture's shape, so the others lay out its tile at once. */
  setCamera = (enabled: boolean, aspect?: number) =>
    this.request((s, ack) =>
      s.emit(
        'request:set-media-state',
        { video: aspect !== undefined ? { enabled, aspect } : { enabled } },
        ack,
      ),
    )
  /** Take the room's presentation slot. The patch carries the share; the media plane the pixels. */
  startScreenShare = (payload: StartScreenSharePayload) =>
    this.request((s, ack) => s.emit('request:start-screen-share', payload, ack))
  /** End our own presentation, or (moderators) someone else's. */
  stopScreenShare = (peerId?: string) =>
    this.request((s, ack) => s.emit('request:stop-screen-share', peerId ? { peerId } : {}, ack))

  /** Transitions only, and never a banner: a leave racing the last one is not worth showing. */
  setSpeaking(speaking: boolean): void {
    this.bump('speakingSent')
    this.socketStore.get()?.emit('request:set-speaking', { speaking }, this.reportAck)
  }

  setQuality(quality: ConnectionQuality): void {
    this.bump('qualitySent')
    this.socketStore.get()?.emit('request:set-quality', { quality }, this.reportAck)
  }

  /**
   * The ack of a fire-and-forget report. A refusal saying we are not in a
   * room while we believe we are is a desync, and the cure is a rejoin.
   * Once at a time.
   */
  private reportAck = (result: AckResult<void>): void => {
    if (result.ok) return
    this.bump('sendFailures')
    this.setDiag({ lastSendError: `${result.error.code}: ${result.error.message}` })
    if (result.error.code === 'not_in_room' && this.inRoom() && !this.healing) {
      this.healing = true
      void this.rejoin().finally(() => {
        this.healing = false
      })
    }
  }

  raiseHand = (raised: boolean) =>
    this.request((s, ack) => s.emit('request:raise-hand', { raised }, ack))
  /** The room's default layout: the host's suggestion; each client's own choice wins over it. */
  setLayout = (mode: LayoutMode) =>
    this.request((s, ack) => s.emit('request:set-layout', { mode }, ack))
  setFocus = (peerId: PeerId | null) =>
    this.request((s, ack) => s.emit('request:set-focus', { peerId }, ack))
  /** Host switch: whoever talks for a while uninterrupted takes the stage for everyone. */
  setAutoSpotlight = (enabled: boolean) =>
    this.request((s, ack) => s.emit('request:set-auto-spotlight', { enabled }, ack))
  admit = (peerId: PeerId) => this.request((s, ack) => s.emit('lobby:admit', { peerId }, ack))
  deny = (peerId: PeerId) => this.request((s, ack) => s.emit('lobby:deny', { peerId }, ack))
  muteParticipant = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:mute-participant', { peerId }, ack))
  allowUnmute = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:allow-unmute', { peerId }, ack))
  /** A host turns someone's camera off; it stays off until they allow it again. */
  stopVideo = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:stop-participant-video', { peerId }, ack))
  /** Lifts that; the camera stays off and its owner turns it on. */
  allowVideo = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:allow-video', { peerId }, ack))
  /** Everyone below us at once: mute, or cameras off (`off`); or let them turn it back on. */
  forEveryone = (kind: MediaKind, off: boolean) =>
    this.request((s, ack) => s.emit('request:host-all', { kind, off }, ack))
  /** Ask one person (or everyone: null) to turn their mic or camera on. */
  askToTurnOn = (kind: MediaKind, peerId: PeerId | null) =>
    this.request((s, ack) => s.emit('request:ask-media', { kind, peerId }, ack))
  /** Let a participant record the meeting, or take it back. */
  grantRecording = (peerId: PeerId, granted: boolean) =>
    this.request((s, ack) => s.emit('request:grant-recording', { peerId, granted }, ack))
  /** The host's ask, answered or set aside. */
  dismissAsk(): void {
    patch(this.store, { asked: null })
  }
  /**
   * Hosts and cohosts: what attendees may do — turn their mic or camera
   * on, present, ask the assistant. Taking something away turns it off for
   * them now. Remembered for the room's next meeting.
   */
  setPermissions = (changes: Partial<AttendeePermissions>) =>
    this.request((s, ack) => s.emit('request:set-permissions', changes, ack))
  /**
   * Hosts and cohosts: let someone speak — their mic and camera — while
   * attendees may not, or move them back to the audience. They are told.
   */
  grantSpeak = (peerId: PeerId, granted: boolean) =>
    this.request((s, ack) => s.emit('request:grant-speak', { peerId, granted }, ack))
  /**
   * The host: end the meeting for everyone — the recording and the note
   * taker stop, everyone is let go, and the meeting's notes are written.
   * (To go and leave the others to carry on: leave().)
   */
  endForEveryone = () => this.request((s, ack) => s.emit('request:end-meeting', ack))
  /** The invitation's notice, seen. */
  dismissInvite(): void {
    patch(this.store, { invited: null })
  }
  removeParticipant = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:remove-participant', { peerId }, ack))
  /** Moderators: silence one person's chat (they still read), or release them. */
  muteChat = (peerId: PeerId, muted: boolean) =>
    this.request((s, ack) => s.emit('request:mute-chat', { peerId, muted }, ack))
  lowerHand = (peerId: PeerId) =>
    this.request((s, ack) => s.emit('request:lower-hand', { peerId }, ack))

  leave(): void {
    this.socketStore.get()?.emit('room:leave', () => undefined)
    this.sockets.disconnect()
    this.unwatchNetwork?.()
    this.unwatchNetwork = null
    clearMembership(this.session)
    this.socketStore.set(null)
    // The message and role stay: a view may still be showing why we left.
    patch(this.store, {
      slug: null,
      sessionId: null,
      sfu: null,
      link: 'online',
      phase: 'idle',
      room: emptyRoomState(''),
      me: null,
    })
  }
}
